import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppShell from '@/components/layout/AppShell';
import claimService from '@/services/claimService';
import { Claim, ClaimStatus, ClaimType } from '@/types';
import { formatCurrency } from '@/utils/formatters';
import { CLAIM_TYPE_LABELS } from '@/utils/constants';
import ClaimDetailContent from './ClaimDetailContent';

const formatMonthKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
const formatMonthLabel = (date: Date) => {
  const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const getStatusStyles = (status: ClaimStatus) => {
  switch (status) {
    case ClaimStatus.PAID:
    case ClaimStatus.APPROVED:
      return {
        bg: 'bg-green-50 dark:bg-green-900/20',
        text: 'text-green-600 dark:text-green-400',
        badgeBg: 'bg-green-100 dark:bg-green-900/30',
        badgeBorder: 'border-green-200 dark:border-green-800/50',
        badgeText: 'text-green-800 dark:text-green-300',
        icon: 'check_circle',
        label: 'Payé',
        animate: false,
      };
    case ClaimStatus.REJECTED:
      return {
        bg: 'bg-red-50 dark:bg-red-900/20',
        text: 'text-red-600 dark:text-red-400',
        badgeBg: 'bg-red-100 dark:bg-red-900/30',
        badgeBorder: 'border-red-200 dark:border-red-800/50',
        badgeText: 'text-red-800 dark:text-red-300',
        icon: 'cancel',
        label: 'Rejeté',
        animate: false,
      };
    default:
      return {
        bg: 'bg-orange-50 dark:bg-orange-900/20',
        text: 'text-orange-600 dark:text-orange-400',
        badgeBg: 'bg-orange-100 dark:bg-orange-900/30',
        badgeBorder: 'border-orange-200 dark:border-orange-800/50',
        badgeText: 'text-orange-800 dark:text-orange-300',
        icon: 'hourglass_top',
        label: 'En cours',
        animate: true,
      };
  }
};

const getTypeIcon = (type: ClaimType) => {
  switch (type) {
    case ClaimType.PHARMACY: return 'pill';
    case ClaimType.DENTAL: return 'dentistry';
    case ClaimType.HOSPITAL: return 'bed';
    case ClaimType.OPTICAL: return 'eyeglasses';
    case ClaimType.CONSULTATION:
    default: return 'medical_services';
  }
};

const DetailPlaceholder: React.FC = () => (
  <div className="flex h-full min-h-[400px] flex-col items-center justify-center gap-3 text-center px-6">
    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
      <MaterialIcon icon="touch_app" style={{ fontSize: 32 }} />
    </div>
    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Sélectionnez un remboursement</h2>
    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
      Cliquez sur un remboursement dans la liste pour en voir le détail.
    </p>
  </div>
);

const ClaimsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: selectedId } = useParams<{ id: string }>();
  const isDetailView = !!selectedId;

  const [statusFilter, setStatusFilter] = React.useState<ClaimStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = React.useState<ClaimType | 'all'>('all');
  const [periodFilter, setPeriodFilter] = React.useState<string>('all');

  const { data: claims, isLoading } = useQuery({
    queryKey: ['claims', statusFilter, typeFilter],
    queryFn: () => claimService.getClaims({
      status: statusFilter !== 'all' ? statusFilter : undefined,
      type: typeFilter !== 'all' ? typeFilter : undefined
    }),
  });

  const { data: stats } = useQuery({
    queryKey: ['claimStats'],
    queryFn: () => claimService.getClaimStats(),
  });

  const availableMonths = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const claim of claims ?? []) {
      const date = new Date(claim.treatmentDate);
      map.set(formatMonthKey(date), formatMonthLabel(date));
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, label]) => ({ key, label }));
  }, [claims]);

  const filteredClaims = React.useMemo(() => {
    if (periodFilter === 'all') return claims ?? [];
    return (claims ?? []).filter((c) => formatMonthKey(new Date(c.treatmentDate)) === periodFilter);
  }, [claims, periodFilter]);

  const groupedClaims = filteredClaims.reduce((groups, claim) => {
    const month = formatMonthLabel(new Date(claim.treatmentDate));
    (groups[month] ??= []).push(claim);
    return groups;
  }, {} as Record<string, Claim[]>);

  const periodLabel = periodFilter === 'all'
    ? 'Tous'
    : availableMonths.find((m) => m.key === periodFilter)?.label ?? 'Tous';

  const handleBack = () => {
    if (isDetailView) navigate('/claims');
    else navigate('/dashboard');
  };

  return (
    <AppShell
      title={isDetailView ? 'Détail remboursement' : 'Mes Remboursements'}
      showBack
      backLabel={isDetailView ? 'Liste' : 'Accueil'}
      onBack={handleBack}
      wide
    >
      <div className="flex flex-col md:flex-row md:gap-6 md:pt-4">
        {/* List column */}
        <aside
          className={`flex flex-col gap-4 p-4 md:p-0 md:w-[420px] md:shrink-0 md:max-h-[calc(100vh-6rem)] md:overflow-y-auto md:pr-2 ${
            isDetailView ? 'hidden md:flex' : 'flex'
          }`}
        >
          {/* Summary Widget */}
          <section className="flex shrink-0 flex-col gap-2 rounded-2xl p-6 bg-gradient-to-br from-primary/20 to-primary/5 dark:from-primary/30 dark:to-background-dark border border-primary/20 shadow-sm relative overflow-hidden">
            <div aria-hidden="true" className="absolute -right-6 -top-6 size-32 bg-primary/20 rounded-full blur-3xl"></div>
            <div className="relative z-10 flex justify-between items-start">
              <div>
                <p className="text-gray-600 dark:text-gray-300 text-sm font-medium leading-normal mb-1">Total remboursé ce mois</p>
                <p className="text-gray-900 dark:text-white tracking-tight text-3xl font-bold leading-tight">
                  {stats ? formatCurrency(stats.totalReimbursed).replace('XOF', '').trim() : '0'}
                  <span className="text-lg text-gray-500 dark:text-gray-400 font-medium ml-1">XOF</span>
                </p>
              </div>
              <div className="size-10 rounded-full bg-white dark:bg-white/10 flex items-center justify-center text-primary-dark dark:text-primary shadow-sm">
                <MaterialIcon icon="savings" />
              </div>
            </div>
          </section>

          {/* Filters */}
          <div className="flex shrink-0 gap-3 overflow-x-auto no-scrollbar pb-1 -mx-4 px-4 md:mx-0 md:px-0">
            <div className="relative">
              <select
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                aria-label="Filtrer par période"
              >
                <option value="all">Période : Tous</option>
                {availableMonths.map((m) => (
                  <option key={m.key} value={m.key}>{m.label}</option>
                ))}
              </select>
              <button
                type="button"
                className={`flex h-9 shrink-0 items-center justify-center gap-x-2 rounded-full px-4 shadow-sm transition-transform active:scale-95 ${periodFilter !== 'all' ? 'bg-primary text-white' : 'bg-gray-900 dark:bg-white text-white dark:text-background-dark'}`}
              >
                <MaterialIcon icon="calendar_month" style={{ fontSize: 18 }} />
                <p className="text-sm font-medium leading-normal">Période : {periodLabel}</p>
              </button>
            </div>

            <div className="relative">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as ClaimType | 'all')}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                aria-label="Filtrer par type"
              >
                <option value="all">Type : Tous</option>
                {Object.values(ClaimType).map((type) => (
                  <option key={type} value={type}>{CLAIM_TYPE_LABELS[type] ?? type}</option>
                ))}
              </select>
              <button
                type="button"
                className={`flex h-9 shrink-0 items-center justify-center gap-x-2 rounded-full border px-4 transition-transform active:scale-95 ${typeFilter !== 'all' ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-surface-dark border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200'}`}
              >
                <p className="text-sm font-medium leading-normal">
                  Type : {typeFilter === 'all' ? 'Tous' : CLAIM_TYPE_LABELS[typeFilter] ?? typeFilter}
                </p>
                <MaterialIcon icon="keyboard_arrow_down" className={typeFilter !== 'all' ? 'text-white' : 'text-gray-500'} style={{ fontSize: 18 }} />
              </button>
            </div>

            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as ClaimStatus | 'all')}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                aria-label="Filtrer par statut"
              >
                <option value="all">Statut : Tous</option>
                {Object.values(ClaimStatus).map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <button
                type="button"
                className={`flex h-9 shrink-0 items-center justify-center gap-x-2 rounded-full border px-4 transition-transform active:scale-95 ${statusFilter !== 'all' ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-surface-dark border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200'}`}
              >
                <p className="text-sm font-medium leading-normal capitalize">
                  Statut : {statusFilter === 'all' ? 'Tous' : statusFilter}
                </p>
                <MaterialIcon icon="keyboard_arrow_down" className={statusFilter !== 'all' ? 'text-white' : 'text-gray-500'} style={{ fontSize: 18 }} />
              </button>
            </div>
          </div>

          {/* Claims */}
          {isLoading ? (
            <div className="space-y-4 mt-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 w-full bg-gray-100 dark:bg-white/5 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : Object.keys(groupedClaims).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MaterialIcon icon="receipt_long" className="text-4xl text-gray-300 dark:text-gray-600 mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Aucun remboursement pour ces filtres.</p>
            </div>
          ) : (
            Object.entries(groupedClaims).map(([month, monthClaims]) => (
              <section key={month} className="flex flex-col gap-3 mt-2">
                <h3 className="text-xs font-bold uppercase tracking-wider px-1 text-gray-500 dark:text-gray-400">
                  {month}
                </h3>
                <ul className="flex flex-col gap-3">
                  {monthClaims.map((claim) => {
                    const styles = getStatusStyles(claim.status);
                    const isPaid = [ClaimStatus.PAID, ClaimStatus.APPROVED].includes(claim.status);
                    const isSelected = claim.id === selectedId;
                    const awaitingTransmission =
                      claim.agTransmitted === false &&
                      [ClaimStatus.SUBMITTED, ClaimStatus.PENDING].includes(claim.status);

                    return (
                      <li key={claim.id}>
                        <Link
                          to={`/claims/${claim.id}`}
                          aria-current={isSelected ? 'page' : undefined}
                          className={`flex flex-col bg-surface-light dark:bg-surface-dark rounded-2xl p-4 shadow-sm border transition-all active:scale-[0.99] ${
                            isSelected
                              ? 'border-primary ring-2 ring-primary/30'
                              : 'border-gray-100 dark:border-white/5 hover:border-primary/30 hover:shadow-md'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex gap-3 items-center">
                              <div className={`flex items-center justify-center rounded-xl ${styles.bg} shrink-0 size-12 ${styles.text}`}>
                                <MaterialIcon icon={getTypeIcon(claim.type)} style={{ fontSize: 24 }} />
                              </div>
                              <div>
                                <p className="text-gray-900 dark:text-white text-base font-semibold leading-snug">
                                  {claim.providerName}
                                </p>
                                <p className="text-gray-500 dark:text-gray-400 text-xs font-normal mt-0.5">
                                  {new Date(claim.treatmentDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} • Soumise le {new Date(claim.submissionDate ?? claim.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1.5">
                              <div className={`px-2.5 py-1 rounded-lg ${styles.badgeBg} ${styles.badgeBorder} flex items-center gap-1 border`}>
                                <MaterialIcon
                                  icon={styles.icon}
                                  className={`${styles.text.replace('600', '700')} ${styles.animate ? 'animate-pulse' : ''}`}
                                  style={{ fontSize: 14 }}
                                />
                                <span className={`${styles.badgeText} text-xs font-bold uppercase tracking-wide`}>
                                  {styles.label}
                                </span>
                              </div>
                              {awaitingTransmission && (
                                <div
                                  title="Pas encore transmise à votre gestionnaire"
                                  className="px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700/60 flex items-center gap-1"
                                >
                                  <MaterialIcon icon="sync_problem" className="text-amber-700 dark:text-amber-400" style={{ fontSize: 14 }} />
                                  <span className="text-amber-800 dark:text-amber-300 text-xs font-bold uppercase tracking-wide">
                                    En attente
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-white/5">
                            {isPaid ? (
                              <>
                                <div className="flex flex-col">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">Montant payé</span>
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{formatCurrency(claim.amount)}</span>
                                </div>
                                <div className="flex flex-col items-end">
                                  <span className="text-xs text-primary-dark dark:text-primary font-medium mb-0.5">Remboursé</span>
                                  <span className="text-lg font-bold text-gray-900 dark:text-white">
                                    {formatCurrency(claim.reimbursedAmount).replace('XOF', '').trim()} <span className="text-sm text-gray-500 font-normal">XOF</span>
                                  </span>
                                </div>
                              </>
                            ) : claim.status === ClaimStatus.REJECTED ? (
                              <p className="text-xs text-red-600 dark:text-red-400 font-medium flex items-center gap-1">
                                <MaterialIcon icon="info" style={{ fontSize: 14 }} />
                                {claim.rejectionReason || 'Demande rejetée'}
                              </p>
                            ) : (
                              <>
                                <div className="flex flex-col">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">Montant demande</span>
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{formatCurrency(claim.amount)}</span>
                                </div>
                                <div className="flex flex-col items-end">
                                  <span className="text-xs text-orange-500 dark:text-orange-400 mb-0.5">En traitement</span>
                                  <span className="text-lg font-bold text-orange-500 dark:text-orange-400">
                                    {formatCurrency(claim.amount).replace('XOF', '').trim()} <span className="text-sm font-normal">XOF</span>
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </aside>

        {/* Detail column */}
        <section
          className={`flex-1 p-4 md:p-0 md:max-h-[calc(100vh-6rem)] md:overflow-y-auto ${
            isDetailView ? 'flex flex-col' : 'hidden md:flex md:flex-col'
          }`}
        >
          {selectedId ? (
            <ClaimDetailContent id={selectedId} />
          ) : (
            <DetailPlaceholder />
          )}
        </section>
      </div>
    </AppShell>
  );
};

export default ClaimsListPage;
